import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { interval, Observable, of, Subject, timer } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';
import { IDestroy, TakeUntilDestroy } from '@utils/auto-unsubscribe';
import { animate, AnimationBuilder, AnimationFactory, AnimationPlayer, style } from '@angular/animations';
import { CarouselSlideTemplateDirective } from '@shared/components/carousel/directives/carousel-slide-template.directive';

@Component({
  selector: 'app-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@TakeUntilDestroy
export class CarouselComponent implements AfterViewInit, IDestroy {
  private _slides: QueryList<CarouselSlideTemplateDirective>;

  public get slides(): QueryList<CarouselSlideTemplateDirective> {
    return this._slides;
  }

  @ContentChildren(CarouselSlideTemplateDirective)
  public set slides(slides: QueryList<CarouselSlideTemplateDirective>) {
    slides.forEach((el, index) => (el.index = index));
    this._slides = slides;
    this.cdr.detectChanges();
  }

  private _pauseAfterClickDelay = 5000;
  private _isPaused = false;
  private _player?: AnimationPlayer;
  public slideIndicator = 0;
  public slideWidth = 1920;
  public currentSlide = 0;
  public isBrowser = isPlatformBrowser(this.platformId);
  public componentDestroy: () => Subject<any>;

  @Input() public interval = 6000;
  @Input() public autoplay = true;

  @ViewChild('track') public trackRef: ElementRef<HTMLElement>;
  @ViewChild('carousel', { static: true }) public carouselRef: ElementRef<HTMLElement>;

  public resizeObserver$ = this.createCarouselResizeObserver().pipe(
    debounceTime(100),
    filter(val => !!val),
    tap(() => {
      this.slideWidth = this.carouselRef.nativeElement.offsetWidth;
      this.transitionCarousel(0);
    })
  );

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    private r2: Renderer2,
    private builder: AnimationBuilder
  ) {}

  private createCarouselResizeObserver(): Observable<ResizeObserverEntry[] | null> {
    if (isPlatformBrowser(this.platformId)) {
      return new Observable(subscriber => {
        const carouselElement = this.carouselRef.nativeElement;
        const observer = new ResizeObserver(entries => subscriber.next(entries));
        observer.observe(carouselElement);
        return function unsubscribe() {
          observer.unobserve(carouselElement);
        };
      });
    }

    return of(null);
  }

  private makePause(): void {
    if (!this._isPaused) {
      this._isPaused = true;

      timer(this._pauseAfterClickDelay)
        .pipe(takeUntil(this.componentDestroy()))
        .subscribe(() => (this._isPaused = false));
    }
  }

  private buildAnimation(offset: number, time: any) {
    return this.builder.build([animate(time == null ? '250ms ease-in' : 0, style({ transform: `translateX(-${offset}px)` }))]);
  }

  public transitionCarousel(time: any): void {
    const offset = this.currentSlide * (this.carouselRef.nativeElement.offsetWidth ?? 0);
    const myAnimation: AnimationFactory = this.buildAnimation(offset, time);
    this._player = myAnimation.create(this.trackRef.nativeElement);
    this._player.play();
  }

  public ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId) && this.autoplay) {
      interval(this.interval)
        .pipe(
          filter(() => !this._isPaused),
          takeUntil(this.componentDestroy())
        )
        .subscribe(() => {
          this.next();
        });
    }
  }

  public onSwipe(event: object): void {
    this.makePause();
    if ((event as { deltaX: number }).deltaX > 0) {
      this.prev();
    } else {
      this.next();
    }
  }

  public next(): void {
    this.slideIndicator += 1;

    if (this.slideIndicator + 1 > this.slides.length) {
      this.slideIndicator = 0;
    }

    if (this.currentSlide + 1 === this.slides.length) {
      let arr = this.slides.toArray();
      const first = arr.shift();
      arr = arr.concat([first as CarouselSlideTemplateDirective]);
      this.slides.reset(arr);
      this.currentSlide -= 1;
      this.transitionCarousel(0);
    }

    this.currentSlide = (this.currentSlide + 1) % this.slides.length;

    this.transitionCarousel(null);
    this.cdr.detectChanges();
  }

  public prev(): void {
    this.slideIndicator -= 1;

    if (this.slideIndicator < 0) {
      this.slideIndicator = this.slides.length - 1;
    }

    if (this.currentSlide === 0) {
      let arr = this.slides.toArray();
      const last = arr.pop();
      arr = [last as CarouselSlideTemplateDirective].concat(arr);
      this.slides.reset(arr);
      this.currentSlide += 1;
      this.transitionCarousel(0);
    }

    this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.transitionCarousel(null);
    this.cdr.detectChanges();
  }

  public onIndicatorClick(index: number): void {
    this.makePause();

    this.slideIndicator = index;
    this.currentSlide = index;

    const sortTemplates = (a: CarouselSlideTemplateDirective, b: CarouselSlideTemplateDirective) => {
      if (a.index > b.index) return 1;
      if (a.index < b.index) return -1;
      return 0;
    };

    const arr = this.slides.toArray().sort(sortTemplates);
    this.slides.reset(arr);

    this.transitionCarousel(null);
    this.cdr.detectChanges();
  }
}
